import { useState, useCallback, useEffect } from 'react';

let startMouseXPos = null;

export const useHorizontalScroll = (
  currentTranslateX,
  maxTranslateX,
  onScrollChart
) => {
  const [translateX, changeTranslateX] = useState(
    currentTranslateX || maxTranslateX
  );

  useEffect(() => {
    if (maxTranslateX !== null) {
      changeTranslateX(maxTranslateX);
    }
  }, [maxTranslateX]);

  useEffect(() => {
    if (currentTranslateX !== null) {
      changeTranslateX(currentTranslateX);
    }
  }, [currentTranslateX, maxTranslateX]);

  const translateChart = useCallback(
    shiftOffset => {
      if (onScrollChart) {
        onScrollChart(shiftOffset);
      } else {
        changeTranslateX(shiftOffset);
      }
    },
    [translateX, changeTranslateX, onScrollChart, currentTranslateX]
  );

  const getChartOffsetByMousePos = useCallback(
    mousePos => {
      const chartOffsetByMousePos = translateX + mousePos - startMouseXPos;

      return Math.max(Math.min(0, chartOffsetByMousePos), maxTranslateX);
    },
    [maxTranslateX, translateX]
  );

  const handleChartMove = useCallback(
    mousePos => {
      const newItemsListOffset = getChartOffsetByMousePos(mousePos);

      translateChart(newItemsListOffset);
    },
    [getChartOffsetByMousePos, translateChart]
  );

  const handleDocumentMouseMove = useCallback(
    event => {
      event.preventDefault();

      handleChartMove(event.clientX);
    },
    [handleChartMove]
  );

  const handleDocumentTouchMove = useCallback(
    event => {
      handleChartMove(event.touches[0].clientX);
    },
    [handleChartMove]
  );

  const handleChartMouseUp = useCallback(
    event => {
      event.preventDefault();

      document.removeEventListener('mousemove', handleDocumentMouseMove);
      document.removeEventListener('mouseup', handleChartMouseUp);
    },
    [handleDocumentMouseMove]
  );

  const handleItemsListTouchEnd = useCallback(() => {
    document.removeEventListener('touchmove', handleDocumentTouchMove);
    document.removeEventListener('touchend', handleItemsListTouchEnd);
  }, [handleDocumentTouchMove]);

  const handleChartMouseDown = useCallback(
    event => {
      startMouseXPos = event.clientX;

      document.addEventListener('mousemove', handleDocumentMouseMove);
      document.addEventListener('mouseup', handleChartMouseUp);
    },
    [handleDocumentMouseMove, handleChartMouseUp]
  );

  const handleChartTouchStart = useCallback(
    event => {
      startMouseXPos = event.touches[0].clientX;

      document.addEventListener('touchmove', handleDocumentTouchMove);
      document.addEventListener('touchend', handleItemsListTouchEnd);
    },
    [handleDocumentTouchMove, handleItemsListTouchEnd]
  );

  return {
    translateX,
    handleMouseDown: handleChartMouseDown,
    handleTouchStart: handleChartTouchStart,
  };
};

export default useHorizontalScroll;
